import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError, formatAR, parseAR } from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Copy, Pencil, Trash2, Plus, Calculator, Search, X } from "lucide-react";
import { validarCUIL, validarCUIT, formatCuit } from "@/lib/cuitCuil";
import { MonthYearField, DateField } from "@/components/DatePickers";

const PAGE_SIZE = 10;
const norm = (s) => (s == null ? "" : String(s)).toLowerCase();
const matches = (q, ...fields) => fields.some((f) => norm(f).includes(q));

const Pager = ({ page, total, setPage, testid }) => {
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, total);
  if (total <= PAGE_SIZE) return null;
  return (
    <div className="flex items-center justify-between px-3 py-2 border-t border-border text-xs text-slate-600" data-testid={testid}>
      <span data-testid={`${testid}-range`}>Mostrando {from}–{to} de {total}</span>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" className="rounded-none h-7 px-2" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid={`${testid}-prev`}>Anterior</Button>
        <span data-testid={`${testid}-indicator`}>Página {page} de {pages}</span>
        <Button variant="outline" size="sm" className="rounded-none h-7 px-2" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid={`${testid}-next`}>Siguiente</Button>
      </div>
    </div>
  );
};

const TIPOS_CONVENIO = [
  ["comercio", "Comercio (CCT 130/75)"],
  ["fuera_convenio", "Fuera de convenio"],
  ["otro_convenio", "Otro convenio"],
];

const CONVENIO_LABEL = {
  comercio: "Comercio",
  fuera_convenio: "Fuera de convenio",
  otro_convenio: "Otro convenio",
};

// Convert numeric legajo value to editable AR-formatted string for the input; empty for null/0.
const numToStr = (n) => (n === null || n === undefined || n === "" || n === 0 ? "" : formatAR(n));
const pctToStr = (n) => (n === null || n === undefined || n === "" || n === 0 ? ""
  : Number(n).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 3 }));

const EMPTY = {
  legajoNumero: "", nombre: "", cuil: "", fechaIngreso: "", categoria: "",
  sucursal: "", tarea: "", jornada: "", tipoConvenio: "comercio", convenioNombre: "",
  afiliadoOsecac: false, afiliadoSindicato: false, sindicatoPct: "", zonaPct: "",
  formaCobro: "mensual", horasNormalesMes: "", horasJornada: "", lugarPago: "",
  empresa: { razonSocial: "", cuit: "", provincia: "", banco: "", artPct: "", contribSegSocial: "20.40", horasConvenio: "" },
  aportes: { banco: "", periodo: "", fecha: "" },
};

export default function LegajosPage() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [limitStatus, setLimitStatus] = useState({ limit: 0, used: 0, canCreate: true });
  const [usage, setUsage] = useState({});
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [categorias, setCategorias] = useState([]);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);

  const load = () => {
    api.get("/legajos").then((r) => setItems(r.data)).catch(() => {});
    api.get("/legajos/limit-status").then((r) => setLimitStatus(r.data)).catch(() => {});
    api.get("/legajos/usage-status").then((r) => setUsage(r.data.usage || {})).catch(() => {});
  };
  useEffect(() => { load(); api.get("/sueldos/categorias").then((r) => setCategorias(r.data)).catch(() => {}); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (it) => {
    setEditing(it.id);
    setForm({
      ...EMPTY, ...it,
      sindicatoPct: pctToStr(it.sindicatoPct), zonaPct: pctToStr(it.zonaPct),
      horasNormalesMes: numToStr(it.horasNormalesMes), horasJornada: numToStr(it.horasJornada),
      formaCobro: (it.tipoConvenio || "comercio") === "comercio" ? "mensual" : (it.formaCobro || "mensual"),
      empresa: { ...EMPTY.empresa, ...(it.empresa || {}), artPct: pctToStr(it.empresa?.artPct), horasConvenio: numToStr(it.empresa?.horasConvenio), contribSegSocial: it.empresa?.contribSegSocial || "20.40" },
      aportes: { ...EMPTY.aportes, ...(it.aportes || {}) },
    });
    setOpen(true);
  };

  const cuilValid = !form.cuil || validarCUIL(form.cuil);
  const cuitValid = !form.empresa.cuit || validarCUIT(form.empresa.cuit);

  const save = async (e) => {
    e.preventDefault();
    if (!cuilValid || !cuitValid) {
      toast.error("Revisá el CUIL/CUIT: el número ingresado no es válido.");
      return;
    }
    try {
      const payload = {
        ...form,
        sindicatoPct: form.afiliadoSindicato ? parseAR(form.sindicatoPct) : 0,
        zonaPct: parseAR(form.zonaPct),
        horasNormalesMes: parseAR(form.horasNormalesMes),
        horasJornada: form.formaCobro === "por_hora" ? parseAR(form.horasJornada) : 0,
        empresa: { ...form.empresa, artPct: parseAR(form.empresa.artPct), horasConvenio: parseAR(form.empresa.horasConvenio) },
      };
      if (editing) await api.put(`/legajos/${editing}`, payload);
      else await api.post("/legajos", payload);
      toast.success("Legajo guardado");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const clone = async (id) => {
    try { await api.post(`/legajos/${id}/clone`); toast.success("Legajo clonado"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/legajos/${id}`); toast.success("Legajo eliminado"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const query = norm(q).trim();
  const filtered = useMemo(() => (query ? items.filter((it) => matches(query, it.legajoNumero, it.nombre, it.empresa?.razonSocial, it.cuil)) : items), [items, query]);
  useEffect(() => { setPage(1); }, [query]);
  const rows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <AppLayout title="Legajos"
      actions={
        <div className="flex items-center gap-3">
          {limitStatus.limit > 0 && (
            <span className="text-xs text-slate-600" data-testid="legajos-counter">{limitStatus.used}/{limitStatus.limit} empleados</span>
          )}
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} disabled={!limitStatus.canCreate} title={!limitStatus.canCreate ? `Límite de ${limitStatus.limit} empleados alcanzado` : undefined} data-testid="new-legajo-btn" className="rounded-none brand-bg text-white gap-2 disabled:opacity-50"><Plus size={16} /> Nuevo legajo</Button>
          </DialogTrigger>
          <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-head">{editing ? "Editar legajo" : "Nuevo legajo"}</DialogTitle>
              <DialogDescription>Complete los campos. Estos datos se reflejan en la hoja 'Aguinaldo'.</DialogDescription>
            </DialogHeader>
            <form onSubmit={save} className="space-y-4" data-testid="legajo-form">
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="label-tiny">Legajo N°</Label><Input className="mt-1.5 rounded-none" data-testid="leg-numero" value={form.legajoNumero} onChange={(e) => setForm({ ...form, legajoNumero: e.target.value })} /></div>
                <div><Label className="label-tiny">Apellido y Nombre</Label><Input required className="mt-1.5 rounded-none" data-testid="leg-nombre" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} /></div>
                <div>
                  <Label className="label-tiny">CUIL</Label>
                  <Input className={`mt-1.5 rounded-none ${!cuilValid ? "border-destructive focus-visible:ring-destructive" : form.cuil ? "border-green-500" : ""}`} data-testid="leg-cuil" inputMode="numeric" placeholder="20-12345678-3" value={form.cuil} onChange={(e) => setForm({ ...form, cuil: formatCuit(e.target.value) })} />
                  {!cuilValid && <p className="text-xs text-destructive mt-1" data-testid="leg-cuil-error">CUIL inválido — verificá el dígito o el prefijo (20/23/24/27).</p>}
                </div>
                <div><Label className="label-tiny">Fecha de ingreso</Label><Input type="date" className="mt-1.5 rounded-none" data-testid="leg-ingreso" value={form.fechaIngreso || ""} onChange={(e) => setForm({ ...form, fechaIngreso: e.target.value })} /></div>
                <div>
                  <Label className="label-tiny">Tipo CCT (convenio)</Label>
                  <Select value={form.tipoConvenio} onValueChange={(v) => setForm({ ...form, tipoConvenio: v, formaCobro: v === "comercio" ? "mensual" : form.formaCobro })}>
                    <SelectTrigger className="mt-1.5 rounded-none" data-testid="leg-tipoconvenio"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TIPOS_CONVENIO.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {form.tipoConvenio === "otro_convenio" && (
                  <div><Label className="label-tiny">CCT (convenio)</Label><Input className="mt-1.5 rounded-none" data-testid="leg-conveniononbre" value={form.convenioNombre} onChange={(e) => setForm({ ...form, convenioNombre: e.target.value })} /></div>
                )}
                <div>
                  <Label className="label-tiny">Categoría</Label>
                  {form.tipoConvenio === "comercio" && categorias.length > 0 ? (
                    <Select value={form.categoria || undefined} onValueChange={(v) => setForm({ ...form, categoria: v })}>
                      <SelectTrigger className="mt-1.5 rounded-none" data-testid="leg-categoria-select"><SelectValue placeholder="Elegí la categoría de escala" /></SelectTrigger>
                      <SelectContent className="max-h-72">
                        {categorias.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input className="mt-1.5 rounded-none" data-testid="leg-categoria" placeholder="Categoría (para el recibo)" value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })} />
                  )}
                </div>
                <div><Label className="label-tiny">Sucursal / Sección</Label><Input className="mt-1.5 rounded-none" data-testid="leg-sucursal" value={form.sucursal} onChange={(e) => setForm({ ...form, sucursal: e.target.value })} /></div>
                <div><Label className="label-tiny">Tarea / Puesto</Label><Input className="mt-1.5 rounded-none" data-testid="leg-tarea" value={form.tarea} onChange={(e) => setForm({ ...form, tarea: e.target.value })} /></div>
                <div>
                  <Label className="label-tiny">Jornada</Label>
                  <Input className="mt-1.5 rounded-none" data-testid="leg-jornada" value={form.jornada} onChange={(e) => setForm({ ...form, jornada: e.target.value })} />
                </div>
              </div>
              <div className="border-t border-border pt-4">
                <p className="label-tiny mb-3">Datos para liquidación de sueldos</p>
                <div className="grid grid-cols-2 gap-4">
                  {form.tipoConvenio === "comercio" && (
                    <div>
                      <Label className="label-tiny">Afiliado a OSECAC</Label>
                      <Select value={form.afiliadoOsecac ? "si" : "no"} onValueChange={(v) => setForm({ ...form, afiliadoOsecac: v === "si" })}>
                        <SelectTrigger className="mt-1.5 rounded-none" data-testid="leg-osecac"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="si">Sí</SelectItem><SelectItem value="no">No</SelectItem></SelectContent>
                      </Select>
                    </div>
                  )}
                  <div>
                    <Label className="label-tiny">Afiliado al sindicato</Label>
                    <Select value={form.afiliadoSindicato ? "si" : "no"} onValueChange={(v) => setForm({ ...form, afiliadoSindicato: v === "si" })}>
                      <SelectTrigger className="mt-1.5 rounded-none" data-testid="leg-sindicato"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="si">Sí</SelectItem><SelectItem value="no">No</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="label-tiny">Sindicato %</Label>
                    <Input className="mt-1.5 rounded-none" data-testid="leg-sindicato-pct" inputMode="decimal" placeholder="0,00" disabled={!form.afiliadoSindicato} value={form.sindicatoPct} onChange={(e) => setForm({ ...form, sindicatoPct: e.target.value })} />
                  </div>
                  <div>
                    <Label className="label-tiny">Adicional por zona %</Label>
                    <Input className="mt-1.5 rounded-none" data-testid="leg-zona-pct" inputMode="decimal" placeholder="0,00" value={form.zonaPct} onChange={(e) => setForm({ ...form, zonaPct: e.target.value })} />
                  </div>
                  <div>
                    <Label className="label-tiny">Forma de cobro</Label>
                    <Select value={form.formaCobro} onValueChange={(v) => setForm({ ...form, formaCobro: v })}>
                      <SelectTrigger className="mt-1.5 rounded-none" data-testid="leg-forma-cobro"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="mensual">Mensual</SelectItem>{form.tipoConvenio !== "comercio" && <SelectItem value="por_hora">Por hora</SelectItem>}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="label-tiny">Horas normales por mes</Label>
                    <Input className="mt-1.5 rounded-none" data-testid="leg-horas-mes" inputMode="decimal" placeholder="Ej: 192" value={form.horasNormalesMes} onChange={(e) => setForm({ ...form, horasNormalesMes: e.target.value })} />
                  </div>
                  <div>
                    <Label className="label-tiny">Horas por jornada</Label>
                    <Input className="mt-1.5 rounded-none" data-testid="leg-horas-jornada" inputMode="decimal" placeholder="Ej: 8" disabled={form.formaCobro !== "por_hora"} value={form.horasJornada} onChange={(e) => setForm({ ...form, horasJornada: e.target.value })} />
                    {form.formaCobro !== "por_hora" && <p className="text-xs text-muted-foreground mt-1">Se habilita con forma de cobro &quot;Por hora&quot;.</p>}
                  </div>
                  <div>
                    <Label className="label-tiny">Lugar de pago</Label>
                    <Input className="mt-1.5 rounded-none" data-testid="leg-lugar-pago" placeholder="Ej: Rosario" value={form.lugarPago} onChange={(e) => setForm({ ...form, lugarPago: e.target.value })} />
                    <p className="text-xs text-muted-foreground mt-1">Se imprime en el recibo de sueldos (lugar y fecha).</p>
                  </div>
                </div>
              </div>
              <div className="border-t border-border pt-4">
                <p className="label-tiny mb-3">Datos del empleador</p>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="label-tiny">Razón social</Label><Input className="mt-1.5 rounded-none" data-testid="leg-razon" value={form.empresa.razonSocial} onChange={(e) => setForm({ ...form, empresa: { ...form.empresa, razonSocial: e.target.value } })} /></div>
                  <div>
                    <Label className="label-tiny">CUIT</Label>
                    <Input className={`mt-1.5 rounded-none ${!cuitValid ? "border-destructive focus-visible:ring-destructive" : form.empresa.cuit ? "border-green-500" : ""}`} data-testid="leg-cuit" inputMode="numeric" placeholder="30-12345678-9" value={form.empresa.cuit} onChange={(e) => setForm({ ...form, empresa: { ...form.empresa, cuit: formatCuit(e.target.value) } })} />
                    {!cuitValid && <p className="text-xs text-destructive mt-1" data-testid="leg-cuit-error">CUIT inválido — verificá el dígito o el prefijo (20/23/24/27/30/33/34).</p>}
                  </div>
                  <div><Label className="label-tiny">DOMICILIO</Label><Input className="mt-1.5 rounded-none" data-testid="leg-domicilio" value={form.empresa.provincia} onChange={(e) => setForm({ ...form, empresa: { ...form.empresa, provincia: e.target.value } })} /></div>
                  <div>
                    <Label className="label-tiny">ART %</Label>
                    <Input className="mt-1.5 rounded-none" data-testid="leg-art-pct" inputMode="decimal" placeholder="0,00" value={form.empresa.artPct} onChange={(e) => setForm({ ...form, empresa: { ...form.empresa, artPct: e.target.value } })} />
                  </div>
                  <div>
                    <Label className="label-tiny">Contrib. Seg. Social</Label>
                    <Select value={form.empresa.contribSegSocial} onValueChange={(v) => setForm({ ...form, empresa: { ...form.empresa, contribSegSocial: v } })}>
                      <SelectTrigger className="mt-1.5 rounded-none" data-testid="leg-contrib-ss"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="20.40">20,40% (inc. a, Comercio/Servicios)</SelectItem>
                        <SelectItem value="18">18% (inc. b, resto/PyME)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="label-tiny">Horas totales convenio</Label>
                    <Input className="mt-1.5 rounded-none" data-testid="leg-horas-convenio" inputMode="decimal" placeholder="Ej: 192" value={form.empresa.horasConvenio} onChange={(e) => setForm({ ...form, empresa: { ...form.empresa, horasConvenio: e.target.value } })} />
                  </div>
                </div>
              </div>
              <div className="border-t border-border pt-4">
                <p className="label-tiny mb-3">Aportes</p>
                <div className="grid grid-cols-3 gap-4">
                  <div><Label className="label-tiny">Banco</Label><Input className="mt-1.5 rounded-none" data-testid="leg-aportes-banco" value={form.aportes.banco} onChange={(e) => setForm({ ...form, aportes: { ...form.aportes, banco: e.target.value } })} /></div>
                  <div><Label className="label-tiny">Período (mm/aaaa)</Label><MonthYearField testid="leg-aportes-periodo" value={form.aportes.periodo} onChange={(v) => setForm({ ...form, aportes: { ...form.aportes, periodo: v } })} /></div>
                  <div><Label className="label-tiny">Fecha (dd/mm/aaaa)</Label><DateField testid="leg-aportes-fecha" className="mt-1.5 w-full" value={form.aportes.fecha} onChange={(v) => setForm({ ...form, aportes: { ...form.aportes, fecha: v } })} /></div>
                </div>
              </div>
              <Button type="submit" disabled={!form.nombre.trim() || !cuilValid || !cuitValid} data-testid="leg-save" className="w-full rounded-none brand-bg text-white h-11 disabled:opacity-50">Guardar legajo</Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      }>
      {!limitStatus.canCreate && (
        <div className="bg-amber-50 border border-amber-300 text-amber-800 text-sm px-4 py-3 mb-4" data-testid="legajos-limit-banner">
          ⚠ Alcanzaste el máximo de {limitStatus.limit} empleados permitidos. Eliminá un legajo para poder agregar otro.
        </div>
      )}
      {items.length === 0 ? (
        <div className="bg-white border border-border p-12 text-center text-muted-foreground">No hay legajos todavía. Creá el primero.</div>
      ) : (
        <div className="space-y-4">
          <div className="bg-white border border-border p-4">
            <div className="relative max-w-md">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input type="text" placeholder="Buscar por legajo, nombre, empleador o CUIL…" value={q} onChange={(e) => setQ(e.target.value)}
                className="rounded-none pl-9 pr-8 h-9" data-testid="legajos-search" />
              {q && (
                <button type="button" onClick={() => setQ("")} data-testid="legajos-search-clear"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"><X size={14} /></button>
              )}
            </div>
          </div>
          <div className="bg-white border border-border overflow-x-auto">
          {filtered.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground" data-testid="legajos-empty-search">Sin resultados para «{q}».</div>
          ) : (
          <table className="w-full text-sm" data-testid="legajos-table">
            <thead><tr className="border-b border-border text-left">
              {["Legajo N°", "Apellido y Nombre", "Empleador", "Convenio", "CUIL", "Categoría", "Utilizado para", ""].map((h) => <th key={h} className="p-3 label-tiny">{h}</th>)}
            </tr></thead>
            <tbody>
              {rows.map((it) => (
                <tr key={it.id} className="border-b border-border last:border-0 hover:bg-slate-50">
                  <td className="p-3 font-mono text-xs">{it.legajoNumero || "-"}</td>
                  <td className="p-3 font-medium text-slate-900">{it.nombre}</td>
                  <td className="p-3" data-testid={`leg-empleador-${it.id}`}>{it.empresa?.razonSocial || "—"}</td>
                  <td className="p-3" data-testid={`leg-convenio-${it.id}`}>{it.tipoConvenio === "otro_convenio" && it.convenioNombre ? it.convenioNombre : (CONVENIO_LABEL[it.tipoConvenio] || "Comercio")}</td>
                  <td className="p-3 font-mono text-xs">{it.cuil || "-"}</td>
                  <td className="p-3">{it.categoria || "-"}</td>
                  <td className="p-3" data-testid={`leg-usado-${it.id}`}>
                    {usage[it.id] === "aguinaldo" ? <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-800 font-medium">Aguinaldo</span>
                      : usage[it.id] === "sueldo" ? <span className="text-xs px-2 py-0.5 bg-emerald-100 text-emerald-800 font-medium">Sueldo</span>
                      : usage[it.id] === "ambos" ? <span className="text-xs px-2 py-0.5 bg-violet-100 text-violet-800 font-medium">Aguinaldo + Sueldo</span>
                      : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="p-3">
                    <div className="flex gap-1 justify-end">
                      {usage[it.id] === "sueldo" ? (
                        <Button size="icon" variant="ghost" title="Ir a Recibo de Sueldos" data-testid={`calc-${it.id}`} className="rounded-none h-8 w-8" onClick={() => nav(`/app/sueldos?legajo=${it.id}`)}><Calculator size={15} /></Button>
                      ) : usage[it.id] === "aguinaldo" ? (
                        <Button size="icon" variant="ghost" title="Calcular SAC" data-testid={`calc-${it.id}`} className="rounded-none h-8 w-8" onClick={() => nav(`/app/calculadora?legajo=${it.id}`)}><Calculator size={15} /></Button>
                      ) : (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="icon" variant="ghost" title="Elegir calculadora" data-testid={`calc-${it.id}`} className="rounded-none h-8 w-8"><Calculator size={15} /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="rounded-none">
                            <DropdownMenuItem data-testid={`calc-menu-ag-${it.id}`} onClick={() => nav(`/app/calculadora?legajo=${it.id}`)}>Calculadora Aguinaldo</DropdownMenuItem>
                            <DropdownMenuItem data-testid={`calc-menu-su-${it.id}`} onClick={() => nav(`/app/sueldos?legajo=${it.id}`)}>Calculadora Sueldos</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                      <Button size="icon" variant="ghost" title={!limitStatus.canCreate ? `Límite de ${limitStatus.limit} empleados alcanzado` : "Clonar"} disabled={!limitStatus.canCreate} data-testid={`clone-${it.id}`} className="rounded-none h-8 w-8 disabled:opacity-40" onClick={() => clone(it.id)}><Copy size={15} /></Button>
                      <Button size="icon" variant="ghost" title="Editar" data-testid={`edit-${it.id}`} className="rounded-none h-8 w-8" onClick={() => openEdit(it)}><Pencil size={15} /></Button>
                      <Button size="icon" variant="ghost" title="Eliminar" data-testid={`del-${it.id}`} className="rounded-none h-8 w-8 text-destructive" onClick={() => remove(it.id)}><Trash2 size={15} /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          )}
          <Pager page={page} total={filtered.length} setPage={setPage} testid="legajos-pagination" />
          </div>
        </div>
      )}
    </AppLayout>
  );
}
